// src/pages/itsm/Problems.tsx
import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Sun, Moon } from 'lucide-react'
// @ts-ignore
import { supabase } from '../../supabaseClient'
import { useTheme } from '../../hooks/useTheme'
import { T, Card, Pill, Btn, StatTile } from '../../components/ui/UI'

const STATUS_CONFIG: Record<string, { label: string; tone: string }> = {
    open: { label: 'Open', tone: 'info' },
    investigating: { label: 'Investigating', tone: 'warning' },
    known_error: { label: 'Known Error', tone: 'danger' },
    resolved: { label: 'Resolved', tone: 'success' },
    closed: { label: 'Closed', tone: 'neutral' },
}

const STATUS_FLOW: Record<string, string[]> = {
    open: ['investigating'],
    investigating: ['known_error', 'resolved'],
    known_error: ['resolved'],
    resolved: ['closed'],
}

const PRIORITY_TONE: Record<string, string> = { low: 'neutral', medium: 'warning', high: 'danger', urgent: 'violet' }

export default function Problems() {
    const navigate = useNavigate()
    const { theme, toggleTheme } = useTheme()
    const [problems, setProblems] = useState<any[]>([])
    const [agents, setAgents] = useState<any[]>([])
    const [loading, setLoading] = useState(true)
    const [showAdd, setShowAdd] = useState(false)
    const [selected, setSelected] = useState<any>(null)
    const [editing, setEditing] = useState(false)
    const [statusFilter, setStatusFilter] = useState('all')

    const [form, setForm] = useState({
        title: '', description: '', priority: 'medium',
        impact: 'low', assigned_to: '',
        root_cause: '', workaround: '', solution: '',
    })

    useEffect(() => { fetchAll() }, [statusFilter])

    const fetchAll = async () => {
        let query = supabase
            .from('itsm_problems')
            .select('*, itsm_agents!assigned_to(name)')
            .order('created_at', { ascending: false })
        if (statusFilter !== 'all') query = query.eq('status', statusFilter)
        const [{ data: p }, { data: a }] = await Promise.all([query, supabase.from('itsm_agents').select('id, name').eq('is_active', true)])
        setProblems(p || [])
        setAgents(a || [])
        setLoading(false)
    }

    const saveProblem = async () => {
        if (editing && selected) {
            await supabase.from('itsm_problems').update({ ...form, assigned_to: form.assigned_to || null }).eq('id', selected.id)
            setEditing(false)
            setSelected({ ...selected, ...form })
        } else {
            await supabase.from('itsm_problems').insert([{ ...form, assigned_to: form.assigned_to || null }])
            setShowAdd(false)
        }
        setForm({ title: '', description: '', priority: 'medium', impact: 'low', assigned_to: '', root_cause: '', workaround: '', solution: '' })
        fetchAll()
    }

    const changeStatus = async (id: number, status: string) => {
        await supabase.from('itsm_problems').update({ status }).eq('id', id)
        fetchAll()
        if (selected) setSelected({ ...selected, status })
    }

    const toneColor = (tone: string) => tone === 'neutral' ? T.text2 : (T as any)[tone]

    const s = styles

    if (loading) return (
        <div className="jr-app" data-theme={theme} style={s.loading}>Loading...</div>
    )

    return (
        <div className="jr-app" data-theme={theme} style={s.page}>
            <button onClick={() => navigate('/dashboard/itsm')} style={s.back}>← Back to ITSM</button>

            <div style={s.header}>
                <div>
                    <h1 style={s.title}>Problem Management</h1>
                    <p style={s.subtitle}>{problems.length} problems</p>
                </div>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                    <button
                        onClick={toggleTheme}
                        aria-label="Toggle dark mode"
                        style={{ width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 8, border: `1px solid ${T.borderStrong}`, background: T.surface, color: T.text2, cursor: 'pointer' }}
                    >
                        {theme === 'dark' ? <Sun size={15} /> : <Moon size={15} />}
                    </button>
                    <Btn variant="primary" style={undefined} onClick={() => setShowAdd(true)}>+ New Problem</Btn>
                </div>
            </div>

            {/* Stats */}
            <Card style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', padding: 0, marginBottom: 20 }}>
                {[
                    { label: 'Open', value: problems.filter(p => p.status === 'open').length, tone: 'info' },
                    { label: 'Investigating', value: problems.filter(p => p.status === 'investigating').length, tone: 'warning' },
                    { label: 'Known Errors', value: problems.filter(p => p.status === 'known_error').length, tone: 'danger' },
                    { label: 'Resolved', value: problems.filter(p => p.status === 'resolved').length, tone: 'success' },
                ].map((stat, i) => (
                    <StatTile key={i} label={stat.label} value={stat.value} tone={stat.tone as any} />
                ))}
            </Card>

            <div style={{ marginBottom: 16 }}>
                <select style={s.select} value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
                    <option value="all">All Status</option>
                    {Object.entries(STATUS_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                </select>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: selected ? '1fr 380px' : '1fr', gap: 20 }}>
                {/* List */}
                <Card style={{ overflow: 'hidden' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                            <tr style={{ borderBottom: `1px solid ${T.border}` }}>
                                {['#', 'Title', 'Priority', 'Impact', 'Status', 'Assigned', 'Created'].map(h => (
                                    <th key={h} style={s.th}>{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {problems.map(p => {
                                const status = STATUS_CONFIG[p.status] || STATUS_CONFIG.open
                                return (
                                    <tr key={p.id} style={{ borderBottom: `1px solid ${T.border}`, cursor: 'pointer', background: selected?.id === p.id ? T.surface2 : 'transparent' }}
                                        onClick={() => setSelected(p)}>
                                        <td style={s.td}><span className="jr-mono" style={{ fontSize: 11, color: T.text3, fontWeight: 700 }}>{p.problem_number}</span></td>
                                        <td style={s.td}><span style={{ fontSize: 13, fontWeight: 600, color: T.text }}>{p.title}</span></td>
                                        <td style={s.td}><span style={{ fontSize: 11, fontWeight: 700, color: toneColor(PRIORITY_TONE[p.priority]) }}>{p.priority?.toUpperCase()}</span></td>
                                        <td style={s.td}><span style={{ fontSize: 11, fontWeight: 700, color: toneColor(PRIORITY_TONE[p.impact]) }}>{p.impact?.toUpperCase()}</span></td>
                                        <td style={s.td}><Pill tone={status.tone as any}>{status.label}</Pill></td>
                                        <td style={s.td}><span style={{ fontSize: 12, color: T.text2 }}>{p.itsm_agents?.name || '—'}</span></td>
                                        <td style={s.td}><span style={{ fontSize: 11, color: T.text3 }}>{new Date(p.created_at).toLocaleDateString('en-GB')}</span></td>
                                    </tr>
                                )
                            })}
                        </tbody>
                    </table>
                    {problems.length === 0 && <div style={{ textAlign: 'center', padding: '40px', color: T.text3, fontSize: 13 }}>No problems found</div>}
                </Card>

                {/* Detail */}
                {selected && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                        <Card>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 14 }}>
                                <div>
                                    <span className="jr-mono" style={{ fontSize: 11, color: T.text3, fontWeight: 700 }}>{selected.problem_number}</span>
                                    <p style={{ fontSize: 15, fontWeight: 700, color: T.text, marginTop: 4 }}>{selected.title}</p>
                                </div>
                                <div style={{ display: 'flex', gap: 6 }}>
                                    <Btn size="sm" style={undefined} onClick={() => {
                                        setEditing(true)
                                        setForm({ title: selected.title, description: selected.description || '', priority: selected.priority, impact: selected.impact, assigned_to: selected.assigned_to || '', root_cause: selected.root_cause || '', workaround: selected.workaround || '', solution: selected.solution || '' })
                                    }}>Edit</Btn>
                                    <button onClick={() => setSelected(null)} style={{ background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: T.text3 }}>✕</button>
                                </div>
                            </div>

                            {/* Status Flow */}
                            {STATUS_FLOW[selected.status]?.length > 0 && (
                                <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' as const }}>
                                    {STATUS_FLOW[selected.status].map(next => {
                                        const nextTone = STATUS_CONFIG[next]?.tone || 'info'
                                        return (
                                            <button key={next}
                                                style={{ height: 30, padding: '0 12px', background: (T as any)[nextTone + 'Bg'], color: (T as any)[nextTone], border: `1px solid ${(T as any)[nextTone + 'Border']}`, borderRadius: 8, fontSize: 11, fontWeight: 700, cursor: 'pointer' }}
                                                onClick={() => changeStatus(selected.id, next)}>
                                                → {STATUS_CONFIG[next]?.label}
                                            </button>
                                        )
                                    })}
                                </div>
                            )}

                            <table style={{ width: '100%', marginBottom: 12 }}>
                                <tbody>
                                    {[
                                        { label: 'Status', value: STATUS_CONFIG[selected.status]?.label },
                                        { label: 'Priority', value: selected.priority },
                                        { label: 'Impact', value: selected.impact },
                                        { label: 'Assigned', value: selected.itsm_agents?.name || '—' },
                                    ].map(r => (
                                        <tr key={r.label}>
                                            <td style={{ fontSize: 11, color: T.text3, padding: '4px 0', paddingRight: 12, fontWeight: 600 }}>{r.label}</td>
                                            <td style={{ fontSize: 12, color: T.text, fontWeight: 500, padding: '4px 0', textTransform: 'capitalize' }}>{r.value}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>

                            {selected.description && <div style={{ padding: '10px 12px', background: T.surface2, borderRadius: 8, marginBottom: 8 }}><p style={{ fontSize: 11, fontWeight: 700, color: T.text3, textTransform: 'uppercase', marginBottom: 4 }}>Description</p><p style={{ fontSize: 13, color: T.text2, lineHeight: 1.6 }}>{selected.description}</p></div>}
                            {selected.root_cause && <div style={{ padding: '10px 12px', background: T.dangerBg, borderRadius: 8, marginBottom: 8, border: `1px solid ${T.dangerBorder}` }}><p style={{ fontSize: 11, fontWeight: 700, color: T.danger, textTransform: 'uppercase', marginBottom: 4 }}>Root Cause</p><p style={{ fontSize: 13, color: T.text2, lineHeight: 1.6 }}>{selected.root_cause}</p></div>}
                            {selected.workaround && <div style={{ padding: '10px 12px', background: T.warningBg, borderRadius: 8, marginBottom: 8, border: `1px solid ${T.warningBorder}` }}><p style={{ fontSize: 11, fontWeight: 700, color: T.warning, textTransform: 'uppercase', marginBottom: 4 }}>Workaround</p><p style={{ fontSize: 13, color: T.text2, lineHeight: 1.6 }}>{selected.workaround}</p></div>}
                            {selected.solution && <div style={{ padding: '10px 12px', background: T.successBg, borderRadius: 8, border: `1px solid ${T.successBorder}` }}><p style={{ fontSize: 11, fontWeight: 700, color: T.success, textTransform: 'uppercase', marginBottom: 4 }}>Solution</p><p style={{ fontSize: 13, color: T.text2, lineHeight: 1.6 }}>{selected.solution}</p></div>}
                        </Card>
                    </div>
                )}
            </div>

            {/* Add / Edit Modal */}
            {(showAdd || editing) && (
                <div style={s.overlay}>
                    <div style={{ ...s.modal, maxWidth: 560, maxHeight: '90vh', overflowY: 'auto' }}>
                        <h2 style={s.modalTitle}>{editing ? 'Edit Problem' : 'New Problem'}</h2>
                        <div style={s.field}>
                            <label style={s.label}>Title *</label>
                            <input style={s.input} value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} />
                        </div>
                        <div style={s.field}>
                            <label style={s.label}>Description</label>
                            <textarea style={{ ...s.input, height: 70, padding: '8px 10px', resize: 'none' as const }} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} />
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                            <div style={s.field}>
                                <label style={s.label}>Priority</label>
                                <select style={s.input} value={form.priority} onChange={e => setForm({ ...form, priority: e.target.value })}>
                                    <option value="low">Low</option>
                                    <option value="medium">Medium</option>
                                    <option value="high">High</option>
                                    <option value="urgent">Urgent</option>
                                </select>
                            </div>
                            <div style={s.field}>
                                <label style={s.label}>Impact</label>
                                <select style={s.input} value={form.impact} onChange={e => setForm({ ...form, impact: e.target.value })}>
                                    <option value="low">Low</option>
                                    <option value="medium">Medium</option>
                                    <option value="high">High</option>
                                </select>
                            </div>
                        </div>
                        <div style={s.field}>
                            <label style={s.label}>Assigned To</label>
                            <select style={s.input} value={form.assigned_to} onChange={e => setForm({ ...form, assigned_to: e.target.value })}>
                                <option value="">Unassigned</option>
                                {agents.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                            </select>
                        </div>
                        <div style={s.field}>
                            <label style={s.label}>Root Cause</label>
                            <textarea style={{ ...s.input, height: 60, padding: '8px 10px', resize: 'none' as const }} value={form.root_cause} onChange={e => setForm({ ...form, root_cause: e.target.value })} />
                        </div>
                        <div style={s.field}>
                            <label style={s.label}>Workaround</label>
                            <textarea style={{ ...s.input, height: 60, padding: '8px 10px', resize: 'none' as const }} value={form.workaround} onChange={e => setForm({ ...form, workaround: e.target.value })} />
                        </div>
                        <div style={s.field}>
                            <label style={s.label}>Solution</label>
                            <textarea style={{ ...s.input, height: 60, padding: '8px 10px', resize: 'none' as const }} value={form.solution} onChange={e => setForm({ ...form, solution: e.target.value })} />
                        </div>
                        <div style={{ display: 'flex', gap: 10 }}>
                            <Btn variant="ghost" style={{ flex: 1, justifyContent: 'center', border: `1px solid ${T.borderStrong}` }} onClick={() => { setShowAdd(false); setEditing(false) }}>Cancel</Btn>
                            <Btn variant="primary" style={undefined} onClick={saveProblem}>Save</Btn>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

const styles: Record<string, React.CSSProperties> = {
    page: { padding: '32px 24px', maxWidth: 1300, margin: '0 auto' },
    loading: { textAlign: 'center', padding: '60px', color: T.text3 },
    back: { background: 'none', border: 'none', color: T.info, fontSize: 13, cursor: 'pointer', marginBottom: 20, padding: 0, fontWeight: 500 },
    header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 },
    title: { fontSize: 24, fontWeight: 700, color: T.text, marginBottom: 4 },
    subtitle: { fontSize: 13, color: T.text2 },
    select: { height: 36, border: `1px solid ${T.borderStrong}`, borderRadius: 8, padding: '0 10px', fontSize: 13, outline: 'none', background: T.surface, color: T.text2 },
    th: { fontSize: 10, fontWeight: 700, color: T.text3, textTransform: 'uppercase', padding: '10px 12px', textAlign: 'left' as const },
    td: { fontSize: 13, padding: '10px 12px', borderBottom: `1px solid ${T.border}` },
    overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 },
    modal: { background: T.surface, borderRadius: 12, padding: 28, width: '100%' },
    modalTitle: { fontSize: 18, fontWeight: 700, color: T.text, marginBottom: 20 },
    field: { marginBottom: 14 },
    label: { display: 'block', fontSize: 12, fontWeight: 500, color: T.text2, marginBottom: 5 },
    input: { width: '100%', height: 36, border: `1px solid ${T.borderStrong}`, borderRadius: 8, padding: '0 10px', fontSize: 13, color: T.text, outline: 'none', background: T.surface },
}
